/**
 * The one way the screens say "this invoice was sent" (SPEC-christa-contacts-identity R3 AC5, AC6, AC9).
 *
 * The backend decides WHETHER, WHEN and BY WHOM an invoice was sent in one place
 * (services/invoice_send_evidence.py) and sends the result as `send_evidence`. The detail page's Email field, the send
 * history, the invoice list's Sent column and the change review all read it, and all use the same fixed phrases below.
 * Nobody derives "was this sent" from `email_sent_at` or `status_changed_at` on their own.
 */
import { z } from 'zod';

export const sendEvidenceSchema = z.object({
  /** provider_accepted | manual_internal_send_resolution | manual_external_send_assertion | marked_sent | unknown */
  kind: z.string(),
  at: z.string().nullable().optional(),
  /** Who recorded it, as stored (an email address). Never shown to her; `by_name` is. */
  by: z.string().nullable().optional(),
  by_name: z.string().nullable().optional(),
  date_recorded: z.boolean().optional(),
  document_revision_id: z.string().nullable().optional(),
}).passthrough();

export type SendEvidence = z.infer<typeof sendEvidenceSchema>;

export function formatSendDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** True when the invoice counts as sent under the one rule (a send is recorded, with or without a date). */
export function isSent(evidence: SendEvidence | null | undefined): boolean {
  return !!evidence && evidence.kind !== 'unknown';
}

/**
 * The date a column should print for "Sent": the recorded send's date under the one rule, else null. An older server with no
 * `send_evidence` at all still reads `email_sent_at` only. Shared by the invoice list and the saved views so they cannot disagree.
 */
export function recordedSentAt(invoice: {
  send_evidence?: SendEvidence | null;
  email_sent_at?: string | null;
}): string | null {
  if (invoice.send_evidence) return isSent(invoice.send_evidence) ? (invoice.send_evidence.at ?? null) : null;
  return invoice.email_sent_at ?? null;
}

/**
 * The fixed phrase, or null when no send is recorded.
 *  - Keystone email:            "Emailed from Keystone on {date}"
 *  - a person's Mark as Sent:   "Marked as sent by {name} on {date}" (no actor recorded: "Marked as sent on {date}")
 *  - sent with no timestamp:    "Marked as sent (date not recorded)"
 */
export function sendEvidencePhrase(evidence: SendEvidence | null | undefined): string | null {
  if (!isSent(evidence)) return null;
  const ev = evidence as SendEvidence;
  const when = formatSendDate(ev.at);
  if (ev.kind === 'provider_accepted') return when ? `Emailed from Keystone on ${when}` : 'Emailed from Keystone';
  if (!when) return 'Marked as sent (date not recorded)';
  const name = readableActorName(ev.by_name);
  const who = name ? ` by ${name}` : '';
  return `Marked as sent${who} on ${when}`;
}

/** What the line says when the person is not a usable name (a role label or an address): never shown as such. */
export const TEAM_FALLBACK_NAME = 'someone on your team';

const ROLE_LABELS = new Set([
  'admin', 'superadmin', 'administrator', 'support', 'service', 'system', 'mcp', 'noreply', 'donotreply',
  'root', 'bot', 'automation',
]);

/** A role or service label such as "Super Admin" or "no-reply", compared ignoring case and separators. */
export function isRoleLabel(text: string): boolean {
  return ROLE_LABELS.has(text.toLowerCase().replace(/[^a-z0-9]/g, ''));
}

/** The name to print after "by": the server's name, unless it is a role label or an address (an older server). */
export function readableActorName(name: string | null | undefined): string | null {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return null;
  if (trimmed.includes('@') || isRoleLabel(trimmed)) return TEAM_FALLBACK_NAME;
  return trimmed;
}

/** Wording the detail page adds when the content changed after the recorded send (document history says so). */
export const NOT_SENT_SINCE_CHANGE = 'This version has not been sent yet.';

/** R5 AC7: the same fact on an invoice that is already sent, partly paid or paid. "Not sent yet" reads as "send it again". */
export const WORDING_CHANGED_AFTER_SEND = 'The wording changed after this was sent.';

const ISSUED_STATUSES = new Set(['sent', 'partially_paid', 'paid', 'overdue']);

/** The one line for "the content changed after the recorded send", chosen by the invoice's status. */
export function changedAfterSendLine(invoiceStatus: string | null | undefined): string {
  return invoiceStatus && ISSUED_STATUSES.has(invoiceStatus) ? WORDING_CHANGED_AFTER_SEND : NOT_SENT_SINCE_CHANGE;
}

/** The current-version answer from document history: content changed after the recorded send. */
export function contentChangedAfterSend(
  version: { state?: string; reason_code?: string } | null | undefined,
): boolean {
  return version?.state === 'not_yet_sent' && version?.reason_code === 'changed_since_send';
}
